import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import Icon from './Icon';

const STATUS_META = {
  queued:  { label: 'Kuyrukta',   color: '#74746D', icon: 'clock' },
  sent:    { label: 'Gönderildi', color: '#34D399', icon: 'check' },
  opened:  { label: 'Açıldı',     color: '#FF8A3D', icon: 'eye' },
  replied: { label: 'Cevaplandı', color: '#D4FF3A', icon: 'inbox' },
  failed:  { label: 'Başarısız',  color: '#FF6B5B', icon: 'x' },
};

const fmtDate = (d) => d ? new Date(d).toLocaleString('tr-TR', { dateStyle: 'medium', timeStyle: 'short' }) : null;

// Basit placeholder doldurma (anlık önizleme için)
const fillTemplate = (str, vars) =>
  (str || '').replace(/\{\{\s*(\w+)\s*\}\}/g, (_, k) => (vars[k] ?? ''));

export default function LeadEmailDrawer({ lead, mode = 'view', onClose, onChanged }) {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [composing, setComposing] = useState(mode === 'compose');

  // Compose formu
  const [templates, setTemplates] = useState([]);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  const firstName = (lead.contact_name || '').trim().split(/\s+/)[0] || '';
  const vars = {
    first_name: firstName,
    full_name: lead.contact_name || '',
    company_name: lead.company_name || '',
    brand_name: lead.company_name || '',
    title: lead.title || '',
    city: lead.city || '',
    country: lead.country || '',
    email: lead.email || '',
  };

  const fetchEmails = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/leads/${lead.id}/emails`);
      setEmails(res.data);
      if (res.data.length > 0) setExpandedId(res.data[0].id);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [lead.id]);

  useEffect(() => { fetchEmails(); }, [fetchEmails]);

  // Compose moduna geçince şablonları yükle
  useEffect(() => {
    if (composing && templates.length === 0) {
      api.get('/email-templates').then(res => setTemplates(res.data || [])).catch(() => {});
    }
  }, [composing, templates.length]);

  // Roll-up durum (zaman çizelgesi için): kayıtların en ileri durumu
  const hasSent = emails.length > 0 || lead.email_sent;
  const openedAt = emails.find(e => e.opened_at)?.opened_at || lead.email_opened_at;
  const repliedAt = emails.find(e => e.replied_at)?.replied_at || lead.email_received_at;
  const sentAt = emails[emails.length - 1]?.sent_at || lead.email_sent_at;

  const handleSend = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !body.trim()) { setError('Konu ve içerik zorunludur'); return; }
    setSending(true);
    setError(null);
    try {
      await api.post(`/leads/${lead.id}/emails`, {
        subject: fillTemplate(subject, vars),
        body: fillTemplate(body, vars),
      });
      setSubject(''); setBody('');
      setComposing(false);
      await fetchEmails();
      onChanged && onChanged();
    } catch (err) {
      setError(err.response?.data?.message || 'Gönderim hatası');
    } finally {
      setSending(false);
    }
  };

  const applyTemplate = (tplId) => {
    const t = templates.find(x => String(x.id) === String(tplId));
    if (t) { setSubject(t.subject || ''); setBody(t.body || ''); }
  };

  const timeStep = (label, color, ts, active) => (
    <div className="timeline__step">
      <div className="timeline__dot timeline__dot--done" style={active ? { background: color } : { background: 'var(--card)', borderColor: 'var(--border)', color: 'var(--text-muted)' }}>
        {active ? '✓' : ''}
      </div>
      <div className="timeline__label" style={active ? { color } : {}}>
        {label}
        {active && ts && <div style={{ fontSize: 10, marginTop: 2 }}>{fmtDate(ts)}</div>}
      </div>
    </div>
  );

  return (
    <>
      <div className="drawer-overlay" onClick={onClose} />
      <div className="drawer">
        <div className="drawer__header">
          <div style={{ minWidth: 0 }}>
            <h2 className="modal__title" style={{ fontSize: 17, marginBottom: 4 }}>
              {lead.contact_name || 'İsimsiz kişi'}
            </h2>
            <p className="text-muted text-sm" style={{ margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {lead.company_name}{lead.title ? ` · ${lead.title}` : ''}
            </p>
            <p className="text-accent text-sm" style={{ margin: '2px 0 0' }}>{lead.email || '—'}</p>
          </div>
          <button className="drawer__close" onClick={onClose}>×</button>
        </div>

        <div className="drawer__body">
          {/* Durum zaman çizelgesi */}
          {hasSent && (
            <div className="timeline">
              {timeStep('Gönderildi', '#34D399', sentAt, true)}
              <div className={'timeline__bar' + (openedAt ? ' timeline__bar--done' : '')} style={openedAt ? { background: '#FF8A3D' } : {}} />
              {timeStep('Açıldı', '#FF8A3D', openedAt, !!openedAt)}
              <div className={'timeline__bar' + (repliedAt ? ' timeline__bar--done' : '')} style={repliedAt ? { background: '#D4FF3A' } : {}} />
              {timeStep('Cevaplandı', '#D4FF3A', repliedAt, !!repliedAt)}
            </div>
          )}

          {/* Yeni mail butonu */}
          {!composing && (
            <button className="btn btn--primary" style={{ width: '100%', marginBottom: 18 }} onClick={() => setComposing(true)}>
              ＋ Yeni Mail Gönder
            </button>
          )}

          {/* Compose formu */}
          {composing && (
            <form onSubmit={handleSend} className="card" style={{ padding: 16, marginBottom: 18 }}>
              <p style={{ fontWeight: 700, color: 'var(--text)', margin: '0 0 12px' }}>Yeni Mail</p>

              {templates.length > 0 && (
                <div className="form-group">
                  <label className="form-label">Şablondan başlat (opsiyonel)</label>
                  <select className="form-input" defaultValue="" onChange={(e) => applyTemplate(e.target.value)}>
                    <option value="">— Serbest yaz —</option>
                    {templates.map(t => (
                      <option key={t.id} value={t.id}>{t.template_key} ({t.language})</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Konu</label>
                <input className="form-input" value={subject} onChange={(e) => setSubject(e.target.value)}
                  placeholder="Örn: {{company_name}} için fırsat" />
              </div>

              <div className="form-group">
                <label className="form-label">İçerik</label>
                <textarea className="form-input" rows={8} value={body} onChange={(e) => setBody(e.target.value)}
                  placeholder="Merhaba {{first_name}}, ..." style={{ resize: 'vertical' }} />
                <p className="text-muted" style={{ fontSize: 11, margin: '6px 0 0' }}>
                  Placeholder: {'{{first_name}}'}, {'{{company_name}}'}, {'{{title}}'} otomatik doldurulur.
                </p>
              </div>

              {/* Anlık önizleme */}
              {(subject || body) && (
                <div className="card" style={{ padding: 12, marginBottom: 12, background: 'var(--bg)' }}>
                  <p className="text-muted" style={{ fontSize: 11, margin: '0 0 6px' }}>ÖNİZLEME ({lead.email})</p>
                  <p style={{ fontWeight: 600, color: 'var(--text)', margin: '0 0 6px', fontSize: 13 }}>{fillTemplate(subject, vars) || '(konu yok)'}</p>
                  <p style={{ whiteSpace: 'pre-line', fontSize: 12, color: 'var(--text-muted)', margin: 0, maxHeight: 120, overflow: 'auto' }}>{fillTemplate(body, vars)}</p>
                </div>
              )}

              {error && <p style={{ color: 'var(--red)', fontSize: 12, margin: '0 0 10px' }}>{error}</p>}

              <div style={{ display: 'flex', gap: 10 }}>
                <button type="submit" className="btn btn--primary" style={{ flex: 1, opacity: sending ? 0.6 : 1 }} disabled={sending}>
                  {sending ? 'Gönderiliyor...' : '✓ Gönder'}
                </button>
                <button type="button" className="btn btn--secondary" style={{ flex: 1 }} onClick={() => { setComposing(false); setError(null); }}>
                  İptal
                </button>
              </div>
            </form>
          )}

          {/* Mail geçmişi */}
          <p style={{ fontWeight: 700, color: 'var(--text)', margin: '0 0 12px' }}>
            Mail Geçmişi {emails.length > 0 && <span className="text-muted text-sm">({emails.length})</span>}
          </p>

          {loading ? (
            <p className="text-muted text-sm">Yükleniyor...</p>
          ) : emails.length === 0 ? (
            <p className="text-muted text-sm">Bu kişiye henüz mail gönderilmemiş.</p>
          ) : (
            emails.map(em => {
              const meta = STATUS_META[em.status] || STATUS_META.sent;
              const open = expandedId === em.id;
              return (
                <div key={em.id} className="mailcard" onClick={() => setExpandedId(open ? null : em.id)}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                    <div style={{ minWidth: 0 }}>
                      <p style={{ fontWeight: 600, color: 'var(--text)', margin: 0, fontSize: 13 }}>{em.subject}</p>
                      <p style={{ fontSize: 11, margin: '4px 0 0' }}>
                        <span className="text-muted">Kime: </span>
                        <span style={{ color: 'var(--accent)' }}>
                          {em.to_name ? `${em.to_name} ` : ''}&lt;{em.to_email || '—'}&gt;
                        </span>
                      </p>
                      <p className="text-muted" style={{ fontSize: 11, margin: '2px 0 0' }}>{fmtDate(em.sent_at)}</p>
                    </div>
                    <span className="badge" style={{ background: meta.color + '22', color: meta.color, fontWeight: 600, flexShrink: 0 }}>
                      {meta.icon && <Icon name={meta.icon} size={13} />} {meta.label}
                    </span>
                  </div>
                  {open && <div className="mailcard__body">{em.body}</div>}
                </div>
              );
            })
          )}
        </div>
      </div>
    </>
  );
}
